<template>
	<LayoutScreen background="--background-base" max-width="500px">
		<template #content>
			<!-- A plan that is not a commitment keeps the receipt as it was before DEV-35727 (owner, 2026-10-22). -->
			<LayoutFlex
				v-if="classic"
				padding="var(--size-32) var(--size-20) var(--size-24)"
				gap="var(--size-32)"
				align-items="center"
				justify-content="center"
			>
				<div class="done__icon-classic" aria-hidden="true">
					<Icon type="ds_close" size="--size-32" color="--text-accent-rose" />
				</div>

				<LayoutFlex padding="0" gap="var(--size-12)" align-items="center">
					<Text font="--heading-h3" color="--text-dark-primary" text-align="center">
						Your subscription has been cancelled, so you will not be charged in the future.
					</Text>
					<Text font="--text-18-22-reg" color="--text-dark-primary" text-align="center">
						You’ll still be able to use KOVA and continue treatment until your plan ends on
						<strong>{{ endDateLabel }}</strong>
						.
					</Text>
				</LayoutFlex>
			</LayoutFlex>

			<!-- Figma 7415:17715 / 7415:17507: the tile 104 from the top (24 + 80), the text 24 under it. -->
			<LayoutFlex v-else padding="var(--size-24) var(--size-20)" gap="0" align-items="center">
				<LayoutFlex padding="var(--size-80) 0 0" gap="var(--size-24)" align-items="center">
					<div class="done__icon" aria-hidden="true">
						<img class="done__check" :src="checkWhite" alt="" width="20.2515" height="13.2296" />
					</div>

					<LayoutFlex padding="0" gap="var(--size-12)" align-items="center">
						<template v-if="outcomeCopy">
							<Text font="--heading-h3" color="--text-dark-primary" text-align="center">{{ outcomeCopy.title }}</Text>
							<Text
								v-for="line in outcomeCopy.body"
								:key="line"
								font="--text-16-22-reg"
								color="--text-dark-primary"
								text-align="center"
							>
								{{ line }}
							</Text>
						</template>
						<template v-else-if="isCommitmentWindDown">
							<Text font="--heading-h3" color="--text-dark-primary" text-align="center">
								Your subscription has been cancelled and will not renew after your commitment ends.
							</Text>
							<Text font="--text-16-22-reg" color="--text-dark-primary" text-align="center">
								You’ll continue to be charged for your remaining commitment payments. Your plan will end on
								<span class="done__date">{{ endDateLabel }}</span>
							</Text>
						</template>
						<template v-else>
							<Text font="--heading-h3" color="--text-dark-primary" text-align="center">
								Your subscription has been cancelled, so you will not be charged in the future.
							</Text>
							<!-- The full stop must touch the date (prettier split it off: the old "Sep 10 ."). A `hard`
							     cancel ended access with it, so no such line (Anton, design review, 2026-10-27). -->
							<!-- prettier-ignore -->
							<Text v-if="!endedAtCancel" font="--text-16-22-reg" color="--text-dark-primary" text-align="center">
								You’ll still be able to use Kova and continue treatment until your plan ends on
								<span class="done__date">{{ endDateLabel }}</span>.
							</Text>
						</template>
					</LayoutFlex>
				</LayoutFlex>
			</LayoutFlex>
		</template>

		<template #footer>
			<LayoutFlex padding="0 var(--size-20) var(--size-24)" gap="var(--size-16)" align-items="center">
				<Button full-width @ds:click="onGotIt">
					<Text font="--text-16-18-semi">{{ outcomeCopy?.button ?? 'Got it' }}</Text>
				</Button>
				<Text font="--text-16-22-reg" color="--text-dark-primary" text-align="center">
					Any questions?
					<a
						:class="classic ? 'done__support-link-classic' : 'done__support-link'"
						:href="supportMailto"
						@click="onContactSupport"
					>
						Contact support
					</a>
				</Text>
			</LayoutFlex>
		</template>
	</LayoutScreen>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import { Button, Icon, LayoutFlex, LayoutScreen, Text } from '@simple-life-app/growth-ui-kova'
import checkWhite from '@/assets/icons/check-white.svg'
import { formatDate, formatMoney, joinDates, pluralize } from '@/cancelFlow/format'
import { useCancelFlowStore } from '@/store/cancelFlow'
import { useSubscriptionStore } from '@/store/subscription'
import { STEP_TITLE, trackSupportEntryPoint, useCancelStepLogging } from '@/analytics'
import { SUPPORT_EMAIL } from '@/config'

const router = useRouter()
const subscription = useSubscriptionStore()
const flow = useCancelFlowStore()
// Not a commitment (`supply_schedule.term_type` is neither `commitment` nor `upfront`): the old receipt.
const classic = computed(() => !flow.kind)

/**
 * The commitment receipts (DEV-35727, Figma `kova cancel flow1/6`), one per way out of the flow.
 * Built from `cancelFlow.outcome` — the backend answer snapshotted at the cancel call — never from
 * the live subscription, which loses the supply-end schedule once that cancel lands. No outcome →
 * the classic receipt below, exactly as before.
 */
const outcomeCopy = computed<{ title: string; body: string[]; button: string } | null>(() => {
	const o = flow.outcome
	if (!o) return null
	const phase = subscription.getActivePhase
	// " every 4 weeks"; left out when the phase doesn't say.
	const cadence =
		phase?.interval_duration && phase.interval_unit
			? ` every ${pluralize(phase.interval_duration, phase.interval_unit)}`
			: ''
	switch (o.kind) {
		case 'plan_ended':
			return {
				title: 'Your plan has ended',
				body: [
					`We charged ${formatMoney(o.amount, o.currency)} to your current payment method. Your clinicians and the Kova app are available until ${formatDate(o.accessUntil, 'short')}.`,
				],
				button: 'Done',
			}
		case 'ending_after_payments': {
			const last = o.installments[o.installments.length - 1]
			return {
				title: `Your plan is set to end on ${formatDate(last?.date ?? null, 'medium')}`,
				body: [
					`Your remaining payments of ${formatMoney(o.installments[0]?.amount ?? null, o.currency)} are scheduled for ${joinDates(o.installments.map((i) => i.date))}. You won’t be charged again after that.`,
					`You’ll retain access to your clinician and the Kova app until ${formatDate(o.endsOn, 'medium')}.`,
				],
				button: 'Done',
			}
		}
		case 'renewal_off':
			return {
				title: 'Your subscription has been cancelled and will not renew',
				body: [
					`Your scheduled payments of ${formatMoney(o.price, o.currency)}${cadence} will continue until your plan ends on ${formatDate(o.until, 'medium')}. You won’t be charged after that.`,
					'You’ll continue to have access to your care team and the Kova app until then.',
				],
				button: 'Got it',
			}
		case 'renewals_cancelled':
			return {
				title: 'Your future renewals have been canceled',
				body: [
					`Your current plan remains active until ${formatDate(o.until, 'medium')}, with payments of ${formatMoney(o.price, o.currency)}${cadence} until then. After that, you won’t be charged again.`,
					'You’ll continue to have access to your clinicians and the Kova app until then.',
				],
				button: 'Got it',
			}
		// Paid in full (Figma 6847:86618 / 6847:86186).
		case 'refunded':
			return {
				title: 'Your plan has ended',
				body: [
					`You’ll receive a refund of ${formatMoney(o.refund, o.currency)} to your original payment method. Your clinicians and the Kova app will remain available until ${formatDate(o.accessUntil, 'short')}.`,
				],
				button: 'Done',
			}
		case 'paid_through':
			return {
				title: 'Your subscription has been cancelled and will not renew',
				body: [
					`You’ve already paid for your plan through ${formatDate(o.until, 'medium')}. There will be no more charges after your current plan ends.`,
					'You’ll continue to have access to your care team and the Kova app until then.',
				],
				button: 'Got it',
			}
	}
	return null
})

// Show on mount / Done on leave; this screen carries no responses or free text.
useCancelStepLogging(STEP_TITLE.CANCELLATION_CONFIRMED)

const supportMailto = `mailto:${SUPPORT_EMAIL}`

// A commitment that still owes iterations keeps billing after a `soft` cancel, which is what the
// copy above says. The three legs are the backend's own (`determineCancellationType`), read off the
// cancelled subscription: a commitment phase (`commitment_end_at` is non-null only there),
// iterations still owed, and a status the backend left open — a `hard` cancel closes it, and there
// the remaining payments never happen.
const isCommitmentWindDown = computed(() => {
	const sub = subscription.subscription
	if (!sub?.commitment_end_at || (sub.remain_iterations ?? 0) <= 0) return false
	return sub.status === 'active' || sub.status === 'awaiting_approval'
})

// `getAccessEndsAt`, not `current_period_end_at`: a commitment winds down at the end of its
// commitment cycle, so the next charge date would understate the plan's life (see the store).
// A commitment cancelled `soft` (recorded as `soft`, or `soft_commitment` while iterations are owed)
// carries the backend's own end on the answer — `expired_at`, the date /cancel/confirm and the options
// quoted — so that one wins there. So does it after `hard`, on any plan: access ended at the cancel, which
// `expired_at` records; `commitment_end_at` is still returned then but is no access end (DEV-36623).
/** Cancelled `hard`: access ended with the cancel (`expired_at` is then). */
const endedAtCancel = computed(() => subscription.subscription?.cancellation_type === 'hard')

const endDateLabel = computed(() => {
	const sub = subscription.subscription
	const backendEnd = endedAtCancel.value || sub?.commitment_end_at ? sub?.expired_at : null
	const iso = backendEnd ?? subscription.getAccessEndsAt
	if (!iso) return '—'
	const d = new Date(iso)
	if (Number.isNaN(d.getTime())) return iso
	return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
})

function onGotIt() {
	// Offer feedback collection after acknowledgement, matching the Figma flow.
	// Survey answers were already cleared by `Confirm.onSubmit` after the cancel call
	// succeeded, so there is no local reset to do here.
	router.push('/cancel/feedback')
}

function onContactSupport() {
	trackSupportEntryPoint('web_subscription_has_been_cancelled')
}
</script>

<style scoped>
/* The receipt before DEV-35727, kept for a plan that is not a commitment: the rose cross tile. */
.done__icon-classic {
	width: 104px;
	height: 104px;
	display: flex;
	align-items: center;
	justify-content: center;
	background: var(--background-rose-alpha-low, rgba(203, 49, 82, 0.1));
	border-radius: var(--size-36);
}

/* Its link as it rendered then: DS `.text a { color: inherit }` kept it the text colour. */
.done__support-link-classic {
	text-decoration: underline;
}

/* Figma "Icon": 64×64, radius 24, `brand/shot/shot-primary` — every receipt has the green tick. */
.done__icon {
	display: flex;
	align-items: center;
	justify-content: center;
	width: var(--size-64);
	height: var(--size-64);
	background: var(--background-brand-high);
	border-radius: var(--size-24);
}

/* The 24px "Quick Track Icon" tick, drawn upside down in the asset; the frame flips it. */
.done__check {
	transform: scaleY(-1);
}

.done__date {
	font-weight: var(--fw-semi);
}

/* DS Text ships `.text a { color: inherit }` at (0,2,1): the `.text a` prefix lets the brand green win. */
.text a.done__support-link {
	color: var(--background-brand-high);
	text-decoration: underline;
}
</style>
